import React, { useEffect, memo, useState, useCallback, useRef } from 'react'
import { useJsApiLoader, GoogleMap } from '@react-google-maps/api'
import PropTypes from 'prop-types'
import {
  MapMarker,
  MapPopup,
  BackButton,
  SetMapBound,
  MapResizeListener
} from './components'
import { darkMapStyles, lightMapStyles } from './mapStyles'
import mapStyleModule from './KhubMap.module.css'
import { GoogleMapApiScriptOptions } from '../../api'

const mapOptions = {
  fullscreenControl: false,
  gestureHandling: 'none',
  panControl: false,
  zoomControl: false,
  disableDefaultUI: true,
  // styles: darkMapStyles,
  restriction: {
    // Bound to indonesia
    latLngBounds: {
      north: 4.463729,
      east: 140.990397,
      south: -9.756614,
      west: 95.582856,
    },
  }
}

// Google Maps Component
const MapComponent = ({
  data,
  withShadow,
  style,
  mapStyleColor,
  mapClassName,
  additionalOptions,
  onRedirect,
  children,
  onLoad
}) => {
  const options = {
    ...mapOptions,
    styles: mapStyleColor === 'light' ? lightMapStyles : darkMapStyles,

    ...additionalOptions
  }
  const markerHoverTimeout = useRef(false)
  const { isLoaded, loadError } = useJsApiLoader(GoogleMapApiScriptOptions)

  const [breadcrumbMarkers, setbreadcrumbMarkers] = useState([])
  const [visibleMarkers, setVisibleMarkers] = useState([])
  const [activeMarker, setActiveMarker] = useState(null)

  // Set initial visible markers
  useEffect(() => {
    setVisibleMarkers(data)
  }, [data])

  // On Map click
  const onMapClick = useCallback(
    (e) => {
      // check if there is an active marker, close it
      if (activeMarker) {
        setActiveMarker(null)
      }
    },
    [activeMarker]
  )

  // On Marker Click
  const onMarkerClick = useCallback(
    (event, data) => {
      event.stopPropagation()
      if ('places' in data && data.places.length > 0) {
        // array of array for determining the parent markers when back button pressed
        setbreadcrumbMarkers([
          ...breadcrumbMarkers,
          [...visibleMarkers]
        ])
        setActiveMarker(null)
        setVisibleMarkers(data.places)
      }

      if(onRedirect){
        onRedirect()
      }
    },
    [breadcrumbMarkers, visibleMarkers, onRedirect]
  )

  // Marker click on mobile
  const onMarkerMobileClick = useCallback(
    (event, data) => {
      event.stopPropagation()
      // Id markerData doesn't have metadata, do not show the popup
      if ('noInteraction' in data === false) {
        setActiveMarker(data)

        // If user click on the same marker twice, show the places under that marker
        if (data === activeMarker) {
          if ('places' in data && data.places.length > 0) {
            // array of array for determining the parent markers when back button pressed
            setbreadcrumbMarkers([
              ...breadcrumbMarkers,
              [...visibleMarkers]
            ])
            setActiveMarker(null)
            setVisibleMarkers(data.places)
          }
        }
      }
    },
    [activeMarker, breadcrumbMarkers, visibleMarkers]
  )

  // Marker mouseover
  const onMarkerOver = useCallback(
    (markerData) => {
      clearTimeout(markerHoverTimeout.current)

      // If markerData doesn't have metadata, do not show the popup
      if ('noInteraction' in markerData === false) {
        setActiveMarker(markerData)
      }
    },
    []
  )

  // Marker mouseout
  const onMarkerOut = useCallback(
    () => {
      // Add timeout so user can hover inside the marker infowindow without closing it
      markerHoverTimeout.current = setTimeout(() => {
        setActiveMarker(null)
      }, 500)
    },
    []
  )

  // On Back Button Click
  const onBackButtonClick = useCallback(
    () => {
      if (breadcrumbMarkers.length > 0) {
        // Get the last markers in breadcrumbs
        setVisibleMarkers(breadcrumbMarkers[breadcrumbMarkers.length - 1])

        // Remove the last markers breadcrumbs
        setbreadcrumbMarkers(breadcrumbMarkers.slice(0, -1))

        setActiveMarker(null)
      }
    },
    [breadcrumbMarkers]
  )

  const renderMap = () => {
    return (
      <GoogleMap
        mapContainerStyle={style}
        clickableIcons={false}
        options={options}
        mapContainerClassName={`${mapStyleModule.map} ${mapClassName}`}
        onClick={onMapClick}
        onLoad={onLoad}
      >
        <MapResizeListener markers={visibleMarkers} breadcrumbs={breadcrumbMarkers} />
        <SetMapBound markers={visibleMarkers} breadcrumbs={breadcrumbMarkers} />
        {visibleMarkers.map(data => (
          <MapMarker
            key={data.id}
            data={data}
            onClick={onMarkerClick}
            onMobileClick={onMarkerMobileClick}
            onMouseOver={onMarkerOver}
            onMouseOut={onMarkerOut}
            isActive={activeMarker === data}
          />
        ))}

        {activeMarker &&
          <MapPopup
            data={activeMarker}
            onMouseOver={onMarkerOver}
            onMouseOut={onMarkerOut}
          />
        }

        <div className="absolute w-full z-10 map-content pointer-events-none">

          { children }

          <div className="container mx-auto pointer-events-none">
            <BackButton breadcrumbs={breadcrumbMarkers} onClick={onBackButtonClick} />
            { withShadow && (
              <React.Fragment>
                <div className={mapStyleModule['shadow-top']}></div>
                <div className={mapStyleModule['shadow-right']}></div>
                <div className={mapStyleModule['shadow-bottom']}></div>
                <div className={mapStyleModule['shadow-left']}></div>
              </React.Fragment>
            )}
          </div>
        </div>
      </GoogleMap>
    )
  }

  if (loadError) {
    return <div>Map cannot be loaded right now</div>
  }

  return isLoaded ? renderMap() : <div className={mapStyleModule.map}>Loading...</div>
}

MapComponent.propTypes = {
  data: PropTypes.array.isRequired,
  style: PropTypes.object,
  withShadow: PropTypes.bool,
  mapStyleColor: PropTypes.oneOf(['dark', 'light']),
  mapClassName: PropTypes.string,
}

MapComponent.defaultProps = {
  style: {},
  mapStyleColor: 'dark',
  withShadow: false,
  mapClassName: '',
}

const KhubMap = memo(MapComponent)

export { KhubMap }
